import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useParams, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { getOrderDetails } from '../services/firestoreService';

const Container = styled.div`
  max-width: 800px;
  margin: 0 auto;
  padding: 40px 20px;
`;

const ConfirmationBox = styled.div`
  background-color: #fff;
  border-radius: 8px;
  padding: 30px;
  text-align: center;
  margin-bottom: 40px;
`;

const Icon = styled.div`
  font-size: 48px;
  margin-bottom: 20px;
`;

const Title = styled.h1`
  font-size: 28px;
  margin-bottom: 10px;
`;

const Subtitle = styled.p`
  font-size: 18px;
  color: #555;
  margin-bottom: 20px;
`;

const OrderNumber = styled.p`
  font-size: 16px;
  font-weight: 500;
`;

const Section = styled.div`
  background-color: #ffffff;
  border-radius: 8px;
  padding: 25px;
  margin-bottom: 30px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.05);
`;

const SectionTitle = styled.h2`
  font-size: 20px;
  margin-bottom: 20px;
  padding-bottom: 10px;
  border-bottom: 1px solid #eee;
`;

const OrderItem = styled.div`
  display: flex;
  margin-bottom: 20px;
  padding-bottom: 20px;
  border-bottom: 1px solid #eee;
  
  &:last-child {
    border-bottom: none;
    margin-bottom: 0;
    padding-bottom: 0;
  }
`;

const ItemImage = styled.img`
  width: 80px;
  height: 80px;
  object-fit: cover;
  margin-right: 20px;
`;

const ItemDetails = styled.div`
  flex-grow: 1;
`;

const ItemTitle = styled.h3`
  font-size: 16px;
  margin: 0 0 5px;
`;

const ItemVariant = styled.p`
  font-size: 14px;
  color: #666;
  margin: 0 0 5px;
`;

const ItemPrice = styled.p`
  font-weight: 500;
  margin: 0;
`;

const AddressBlock = styled.div`
  margin-bottom: 20px;
`;

const TotalSection = styled.div`
  margin-top: 20px;
  padding-top: 20px;
  border-top: 1px solid #eee;
`;

const TotalRow = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 10px;
  font-size: 16px;
  
  &:last-child {
    font-size: 18px;
    font-weight: 700;
    margin-top: 10px;
  }
`;

const Button = styled(Link)`
  display: inline-block;
  padding: 15px 30px;
  background-color: #000000;
  color: #fff;
  border: none;
  border-radius: 4px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  text-decoration: none;
  transition: all 0.3s ease;
  text-align: center;
  
  &:hover {
    background-color: #fff;
    color: #000000;
  }
`;

const ButtonContainer = styled.div`
  display: flex;
  justify-content: center;
  margin-top: 30px;
  gap: 20px;
`;

const LoadingMessage = styled.div`
  text-align: center;
  padding: 40px;
  font-size: 18px;
`;

const ErrorMessage = styled.div`
  text-align: center;
  padding: 40px;
  color: #d32f2f;
  font-size: 18px;
`;

const OrderConfirmation = () => {
  const { orderId } = useParams();
  const { currentUser } = useAuth();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  
  useEffect(() => {
    const fetchOrderDetails = async () => {
      if (!orderId) {
        setError('Order ID not provided');
        setLoading(false);
        return;
      }
      
      try {
        const orderData = await getOrderDetails(orderId);
        
        if (!orderData) {
          setError('Order not found');
        } else if (orderData.userId !== currentUser?.uid) {
          setError('You do not have permission to view this order');
        } else {
          setOrder(orderData);
        }
      } catch (err) {
        console.error('Error fetching order details:', err);
        setError('Failed to load order details');
      } finally {
        setLoading(false);
      }
    };
    
    if (currentUser) {
      fetchOrderDetails();
    } else {
      setLoading(false);
      setError('Please log in to view your order');
    }
  }, [orderId, currentUser]);
  
  if (loading) {
    return <LoadingMessage>Loading order details...</LoadingMessage>;
  }
  
  if (error) {
    return <ErrorMessage>{error}</ErrorMessage>;
  }
  
  if (!order) {
    return <ErrorMessage>Order not found</ErrorMessage>;
  }
  
  // Format date (supports Firestore Timestamp, JS Date, or missing)
  let orderDate = '';
  if (order?.createdAt instanceof Date) {
    orderDate = order.createdAt.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
  } else if (order?.createdAt?.seconds) {
    orderDate = new Date(order.createdAt.seconds * 1000).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
  }
  
  return (
    <Container>
      <ConfirmationBox>
        <Icon>✓</Icon>
        <Title>Thank You for Your Order!</Title>
        <Subtitle>Your order has been received and is being processed.</Subtitle>
        <OrderNumber>Order #{orderId.slice(0, 8).toUpperCase()}</OrderNumber>
      </ConfirmationBox>
      
      <Section>
        <SectionTitle>Order Details</SectionTitle>
        <p><strong>Order Date:</strong> {orderDate}</p>
        <p><strong>Order Status:</strong> {order.status.charAt(0).toUpperCase() + order.status.slice(1)}</p>
        
        {order.items.map((item, index) => (
          <OrderItem key={index}>
            <ItemImage src={item.image} alt={item.title} />
            <ItemDetails>
              <ItemTitle>{item.title}</ItemTitle>
              <ItemVariant>Size: {item.size}, Color: {item.color}</ItemVariant>
              <ItemVariant>Quantity: {item.quantity}</ItemVariant>
            </ItemDetails>
            <ItemPrice>${(item.price * item.quantity).toFixed(2)}</ItemPrice>
          </OrderItem>
        ))}
        
        <TotalSection>
          <TotalRow>
            <span>Subtotal</span>
            <span>${order.subtotal.toFixed(2)}</span>
          </TotalRow>
          <TotalRow>
            <span>Tax</span>
            <span>${order.tax.toFixed(2)}</span>
          </TotalRow>
          <TotalRow>
            <span>Shipping</span>
            <span>${order.shipping.toFixed(2)}</span>
          </TotalRow>
          <TotalRow>
            <span>Total</span>
            <span>${order.total.toFixed(2)}</span>
          </TotalRow>
        </TotalSection>
      </Section>
      
      <Section>
        <SectionTitle>Shipping Information</SectionTitle>
        <AddressBlock>
          <p><strong>{order.shippingAddress.fullName}</strong></p>
          <p>{order.shippingAddress.line1}</p>
          {order.shippingAddress.line2 && <p>{order.shippingAddress.line2}</p>}
          <p>{order.shippingAddress.city}, {order.shippingAddress.state} {order.shippingAddress.postalCode}</p>
          <p>{order.shippingAddress.country}</p>
        </AddressBlock>
      </Section>
      
      <Section>
        <SectionTitle>Payment Information</SectionTitle>
        <p><strong>{order.paymentMethod.type.toUpperCase()}</strong> ending in {order.paymentMethod.last4}</p>
        <p>Cardholder: {order.paymentMethod.cardholderName}</p>
      </Section>
      
      <ButtonContainer>
        <Button to="/account">View All Orders</Button>
        <Button to="/">Continue Shopping</Button>
      </ButtonContainer>
    </Container>
  );
};

export default OrderConfirmation;
